import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ProposalWizard } from "../components/proposals/ProposalWizard";
import { ProposalCard } from "../components/proposals/ProposalCard";
import { ProposalDetailDialog } from "../components/proposals/ProposalDetailDialog";
import { ConfirmDialog } from "../components/shared/ConfirmDialog";
import { Plus, FileText, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const EDITOR_ROLES = ["admin", "rd_lead", "open_innovation_lead", "analyst"];
const DIRECTOR_EMAIL = "dredondo@valorizasm.com";

const STATUS_TABS = [
  { value: "all", label: "Todas" },
  { value: "draft", label: "Borradores" },
  { value: "pending_review", label: "Pendientes" },
  { value: "approved", label: "Aprobadas" },
  { value: "rejected", label: "Rechazadas" },
];

export default function ProposalsPage() {
  const { token, user } = useAuth();
  const [proposals, setProposals] = useState([]);
  const [stats, setStats] = useState(null);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailProposal, setDetailProposal] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const canEdit = EDITOR_ROLES.includes(user?.role);
  const isReviewer = user?.role === "admin" || (user?.email || "").toLowerCase() === DIRECTOR_EMAIL;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, statsData] = await Promise.all([api.getProposals(token), api.getProposalStats(token)]);
      setProposals(list);
      setStats(statsData);
    } catch (e) {
      toast.error("Error al cargar propuestas");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadEntities = useCallback(async () => {
    try {
      const [initiatives, pilots, grantCalls, grantsRadar] = await Promise.all([
        api.getInitiatives(token).catch(() => []),
        api.getPilots(token).catch(() => []),
        api.getGrantCalls(token).catch(() => []),
        api.getGrantsRadar(token).catch(() => []),
      ]);
      const list = [
        ...(initiatives || []).map((i) => ({ type: "initiative", id: i.id, name: i.name || i.title })),
        ...(pilots || []).map((p) => ({ type: "pilot", id: p.id, name: p.name })),
        ...(grantCalls || []).map((g) => ({ type: "grant", id: g.id, name: g.name || g.title })),
        ...(Array.isArray(grantsRadar) ? grantsRadar : grantsRadar?.items || []).map((g) => ({ type: "grant", id: g.id, name: g.title || g.name })),
      ].filter((e) => e.id && e.name);
      setEntities(list);
    } catch (e) {
      // silencioso — el picker simplemente quedará vacío
    }
  }, [token]);

  useEffect(() => { load(); loadEntities(); }, [load, loadEntities]);

  const filtered = tab === "all" ? proposals : proposals.filter((p) => p.status === tab);

  const handleDownload = async (p) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${API_URL}/api/proposals/${p.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("PDF error");
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${p.title}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Error al descargar el PDF");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.deleteProposal(token, deleteTarget.id);
      toast.success("Propuesta eliminada");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="proposals-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><FileText className="w-6 h-6 text-emerald-700" />Propuestas</h1>
          <p className="text-sm text-muted-foreground">Fichas de propuesta con branding corporativo — vinculadas a iniciativas, pilotos y ayudas.</p>
        </div>
        {canEdit && (
          <Button onClick={() => { setEditing(null); setWizardOpen(true); }} data-testid="proposal-new-btn">
            <Plus className="w-4 h-4 mr-2" />Nueva propuesta
          </Button>
        )}
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="proposal-stats-cards">
          <StatCard label="Total" value={stats.total} />
          <StatCard label="Borradores" value={stats.draft} />
          <StatCard label="Pendientes" value={stats.pending_review} />
          <StatCard label="Aprobadas" value={stats.approved} />
          <StatCard label="Rechazadas" value={stats.rejected} />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {STATUS_TABS.map((t) => <TabsTrigger key={t.value} value={t.value} data-testid={`proposal-tab-${t.value}`}>{t.label}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground" data-testid="proposals-empty-state">
          No hay propuestas {tab !== "all" ? "en este estado" : "todavía"}.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="proposals-grid">
          {filtered.map((p) => (
            <ProposalCard
              key={p.id} proposal={p} canEdit={canEdit}
              onView={setDetailProposal}
              onEdit={(pr) => { setEditing(pr); setWizardOpen(true); }}
              onDelete={setDeleteTarget}
              onDownload={handleDownload}
            />
          ))}
        </div>
      )}

      <ProposalWizard open={wizardOpen} onOpenChange={setWizardOpen} editing={editing} entities={entities} onSaved={load} />
      <ProposalDetailDialog
        open={!!detailProposal} onOpenChange={(v) => !v && setDetailProposal(null)}
        proposal={detailProposal} isReviewer={isReviewer} onDownload={handleDownload}
        onChanged={() => { load(); setDetailProposal(null); }}
      />
      <ConfirmDialog
        open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}
        title="Eliminar propuesta" description={`¿Eliminar "${deleteTarget?.title}"? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar" onConfirm={handleDelete} loading={deleting} testId="proposal-delete-confirm"
      />
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <Card><CardContent className="p-3 text-center">
      <div className="text-2xl font-bold">{value ?? 0}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </CardContent></Card>
  );
}
